import React, { useRef } from "react";
import style from "@/styles/pages/Address/Address.module.css";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect, useState } from "react";
import ManualAddress from "@/components/Address/ManualAddress";
import { useDispatch, useSelector } from "react-redux";
import {
  setStreetNumber,
  setStreetName,
  setUnitNumber,
  setCity,
  setProvince,
  setPostalCode,
  setCountry,
  selectHomeAddress,
  setMailingAddressSame,
  setStreetDirection,
  setStreetType,
  setUnitType,
  setAddressId,
} from "@/slices/homeAddressSlice";
import { setAddressComplete, setCredentialsComplete } from "@/slices/progressSlice";
import { useNavigate } from "react-router-dom";
import { selectApplication, setApplicationProgress } from "@/slices/applicationSlice";
import { setResidenceMonths, setResidenceYears, setResidentStatus } from "@/slices/applicantSlice";
import { RootState } from "@/store/dtm";
import { addressInfo, clearPreviousAddressState } from "@/slices/PreviousAddressSlice";
import SlideTransition from "@/styles/animation/SlideTransition";
import { AddressDto, ApplicationDto } from "@/lib/DTMWebAPIClient.generated";
import { createApiClient } from "@/lib/Requests";
import { streetDirections } from "@/ts/streetDirections";
import { streetTypes } from "@/ts/streetTypes";
import { AnimatePresence, motion } from "framer-motion";
import 'animate.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { setClickInsightEvents, setClickInsightFormErrors } from "@/lib/util";

interface AddressSearchResult {
  value: string,
  label: string;
}

export default function JointAddress() {
  const navigate = useNavigate();
  const [manualAddressShown, setmanualAddressShown] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [addressSelected, setAddressSelected] = useState(false);
  const [addressSelectionError, setAddressSelectionError] = useState(false);

  const [residentStatusText, setResidentStatusText] = useState("");
  const [residenceYearsText, setResidenceYearsText] = useState("");
  const [residenceMonthsText, setResidenceMonthsText] = useState("");
  const [displayError, setDisplayError] = useState(false);
  const [focusErrorType, setFocusErrorType] = useState(0);
  const [highlightError, setHighlightError] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const dispatch = useDispatch();
  const homeAddress = useSelector(selectHomeAddress);
  const application = useSelector(selectApplication).application;
  const credential = useSelector(
    (state: RootState) => state.credential
  );
  const applicant = useSelector(
    (state: RootState) => state.applicant.applicant
  );
  const employment = useSelector(
    (state: RootState) => state.employment.employment
  );
  const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
  const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

  const [currentAddressInfo, setCurrentAddressInfo] = useState<addressInfo>({
    id: 0,
    displayId: "",
    streetNumber: "",
    streetName: "",
    streetType: "",
    streetDirection: "",
    city: "",
    province: "",
    country: "",
    postalCode: "",
    unitType: "",
    unitNumber: "",
    manualAddressEntered: true,
    yearsLivedOn: applicant.residenceYears,
    monthsLivedOn: applicant.residenceMonths
  });

  const methods = useForm({
    defaultValues: {
      autoAddress: "",
      autoMailAddress: "",
      streetNumber: homeAddress.address.streetName,
      streetName: homeAddress.address.streetNumber,
      streetType: homeAddress.address.streetType,
      streetDirection: homeAddress.address.streetDirection,
      unitType: homeAddress.address.unitType,
      unitNumber: homeAddress.address.unitNumber,
      cityName: homeAddress.address.city,
      provinceName: homeAddress.address.province,
      postalCode: homeAddress.address.postalCode,
      country: homeAddress.address.country,
      isMailingAddressSame: true,
      residentStatus: "",
      residenceYears: "",
      residenceMonths: ""
    },
  });

  const [residentStatus, residenceYears, residenceMonths] = methods.watch(["residentStatus", "residenceYears", "residenceMonths"])

  const fields = [
    {
      element: "search",
      field: "",
      mode: window.pca?.fieldMode.SEARCH,
    },
    {
      element: "span-street-address",
      field: "Line1",
      mode: window.pca?.fieldMode.POPULATE,
    },
    {
      element: "span-street-address2",
      field: "Line2",
      mode: window.pca?.fieldMode?.POPULATE,
    },
    { element: "city", field: "City", mode: window.pca?.fieldMode.POPULATE },
    {
      element: "span-state",
      field: "ProvinceCode",
      mode: window.pca?.fieldMode.POPULATE,
    },
    {
      element: "span-postcode",
      field: "PostalCode",
      mode: window.pca?.fieldMode.POPULATE,
    },
    {
      element: "span-country",
      field: "CountryName",
      mode: window.pca?.fieldMode.POPULATE,
    },
  ];
  const options = {
    key: "XD95-YY77-BP99-EM55",
  };

  const toggleManualAddress = () => {
    setmanualAddressShown(!manualAddressShown);
    if (!manualAddressShown) {
      // dispatch(clearAddressState());
      formatAddress();
      setShowAddress(false);
      console.log(currentAddressInfo);
    } else {
      if (currentAddressInfo != undefined && currentAddressInfo.streetNumber.trim() != "") {
        methods.setValue("autoAddress", currentAddressInfo.streetNumber + " " + currentAddressInfo.streetName + " " + currentAddressInfo.streetType
          + (homeAddress.address.streetDirection.trim() != "" ? (" " + homeAddress.address.streetDirection) : ""));
        setShowAddress(true);
      }
    }
  };

  const residentStatusChangeHandler = (e: any) => {
    setResidentStatusText(e.target.value);
    methods.setValue("residentStatus", e.target.value);
    methods.clearErrors("residentStatus");
  }

  const residenceYearsChangeHandler = (e: any) => {
    if (e.target.value.charAt(0) == "0") {
      e.target.value = e.target.value.slice(1, 2);
    } else {
      e.target.value = e.target.value.slice(0, 2);
    }
    setResidenceYearsText(e.target.value);
    methods.clearErrors("residenceYears");
    methods.setValue("residenceYears", e.target.value);

    if (methods.getValues("residenceMonths").trim() == "") {
      methods.setValue("residenceMonths", "0");
    }

    if (methods.formState.errors == undefined || Object.keys(methods.formState.errors).length == 0) {
      setDisplayError(false);
    }
  }

  const residenceMonthsChangeHandler = (e: any) => {
    if (e.target.value.charAt(0) == "0") {
      e.target.value = e.target.value.slice(1, 2);
    } else {
      e.target.value = e.target.value.slice(0, 2);
    }
    if (+e.target.value > 11) {
      e.target.value = "11"
    }
    setResidenceMonthsText(e.target.value);
    methods.clearErrors("residenceMonths");
    methods.setValue("residenceMonths", e.target.value);

    if (methods.formState.errors == undefined || Object.keys(methods.formState.errors).length == 0) {
      setDisplayError(false);
    }
  }

  useEffect(() => {
    document.title = "Where do you live?";

    setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);
    //*set profile section complete
    dispatch(setCredentialsComplete(true));

    const control = new window.pca.Address(fields, options);
    control.listen("populate", (address: any) => {
      methods.setValue("autoAddress", address.Line1);
      setAddressSelected(true);
      let streetInfo = address.Line1.split(" ");
      let streetName = "";
      let streetDirection = "";
      let streetType = "";
      let streetComponents: string[] = address.Street.split(" ");
      let lastStreetComponent = streetComponents[streetComponents.length - 1].toUpperCase();

      //Check if street direction and type exists in the street name
      if (streetDirections.includes(lastStreetComponent)) {
        streetDirection = lastStreetComponent;
        let secondLastStreetComponent = streetComponents[streetComponents.length - 2].toUpperCase();
        if (streetTypes.includes(secondLastStreetComponent)) {
          streetType = secondLastStreetComponent;
        }
        streetComponents.forEach((value, index) => {
          if (index < streetComponents.length - 2) {
            streetName += value + " "
          }
        });
      } else if (streetTypes.includes(lastStreetComponent)) {
        streetType = lastStreetComponent;
        streetComponents.forEach((value, index) => {
          if (index != streetComponents.length - 1) {
            streetName += value + " "
          }
        });
      } else {
        streetComponents.forEach((value, index) => {
          console.log(value, index);
          streetName += value + " "
        });
      }
      streetName = streetName.trim();

      //Check if unit number is embeded in the street number
      let streetNumber = "";
      let streetNumberComponents = streetInfo[0].split("-");
      console.log(streetInfo[0].split("-"));
      if (streetNumberComponents.length > 1) {
        streetNumber = streetNumberComponents[streetNumberComponents.length - 1];
      } else {
        streetNumber = streetInfo[0];
      }

      let addressInfo: addressInfo = {
        id: 0,
        displayId: "",
        streetNumber: streetNumber,
        streetName: streetName,
        streetType: streetType,
        streetDirection: streetDirection,
        city: address.City,
        province: address.ProvinceCode,
        country: address.CountryName,
        postalCode: address.PostalCode,
        unitType: address.SubBuilding != undefined && address.SubBuilding != "" ? "unit" : "",
        unitNumber: address.SubBuilding != undefined ? address.SubBuilding : "",
        manualAddressEntered: false,
        yearsLivedOn: applicant.residenceYears,
        monthsLivedOn: applicant.residenceMonths
      }
      setCurrentAddressInfo(addressInfo);

      if (address.SubBuilding != undefined && address.SubBuilding != "") {
        methods.setValue("unitNumber", addressInfo.unitNumber);
        methods.setValue("unitType", "unit");
        methods.register("unitType", {
          required: "Select a unit type",
          pattern: {
            value: /^[ #a-zA-Z0-9._-]+$/,
            message: "Select a unit type",
          },
          validate: {
            unitType: (c) => {
              if (c.trim() != "") {
                return true
              } else {
                return "Select a unit type";
              }
            },
          },
        });
      }

      methods.setValue("streetNumber", addressInfo.streetNumber);
      methods.setValue("streetName", addressInfo.streetName);
      methods.setValue("streetType", addressInfo.streetType);
      methods.setValue("streetDirection", addressInfo.streetDirection);
      methods.setValue("cityName", addressInfo.city);
      methods.setValue("provinceName", addressInfo.province);
      methods.setValue("postalCode", addressInfo.postalCode);
      methods.setValue("country", addressInfo.country);
      methods.clearErrors();

      //If Street Type is not in the master list, toggle manual address entry
      if (!streetTypes.includes(addressInfo.streetType)) {
        toggleManualAddress();
      }
    });

    client.apiApplicationsFetch().then((result) => {
      result.applicant = applicant;
      result.employment = employment;
      result.application = application;
      client.apiApplicationsSave(new ApplicationDto({
        applicationData: JSON.stringify(result)
      })).catch((error) => {
        console.log("Error saving application info");
      });
    }).catch((error) => {
      console.log("Error fetching application info");
    });

    methods.setValue("residentStatus", applicant.residentStatus);
    setResidentStatusText(applicant.residentStatus);
    methods.setValue("residenceYears", applicant.residenceYears != 0 ? applicant.residenceYears.toString() : "0");
    setResidenceYearsText(applicant.residenceYears != 0 ? applicant.residenceYears.toString() : "0")
    methods.setValue("residenceMonths", applicant.residenceMonths != 0 ? applicant.residenceMonths.toString() : "0");
    setResidenceMonthsText(applicant.residenceMonths != 0 ? applicant.residenceMonths.toString() : "0")

    if (homeAddress != undefined && homeAddress.address != undefined && homeAddress.address.city != "" && !manualAddressShown) {
      try {
        const span = document.getElementById("span-street-address");
        if (span != null) {
          span.innerText = (homeAddress.address.unitNumber != "" ? homeAddress.address.unitNumber + "-" : "") +
            homeAddress.address.streetNumber + " " + homeAddress.address.streetName + " "
            + homeAddress.address.streetType + (homeAddress.address.streetDirection.trim() != "" ? (" " + homeAddress.address.streetDirection) : "");
          methods.setValue("autoAddress", span.innerText);
        }
        const span2 = document.getElementById("span-city");
        if (span2 != null) {
          span2.innerText = homeAddress.address.city;

        }
        const span3 = document.getElementById("span-state");
        if (span3 != null) {
          span3.innerText = homeAddress.address.province != undefined ? homeAddress.address.province : "";

        }
        const span4 = document.getElementById("span-postcode");
        if (span4 != null) {
          span4.innerText = homeAddress.address.postalCode != undefined ? homeAddress.address.postalCode : "";

        }
        const span5 = document.getElementById("span-country");
        if (span5 != null) {
          span5.innerText = homeAddress.address.country;
        }
      } catch (e) { }

      if (!applicant.isProspect) {
        setAddressSelected(true);
        toggleManualAddress();
      }

      let addressInfo: addressInfo = {
        id: 0,
        displayId: "",
        streetNumber: homeAddress.address.streetNumber,
        streetName: homeAddress.address.streetName,
        streetType: homeAddress.address.streetType,
        streetDirection: homeAddress.address.streetDirection,
        city: homeAddress.address.city,
        province: homeAddress.address.province,
        country: homeAddress.address.country,
        postalCode: homeAddress.address.postalCode,
        unitType: homeAddress.address.unitType,
        unitNumber: homeAddress.address.unitNumber,
        manualAddressEntered: false,
        yearsLivedOn: applicant.residenceYears,
        monthsLivedOn: applicant.residenceMonths
      }
      setCurrentAddressInfo(addressInfo);
      setShowAddress(true);
      setAddressSelectionError(false);
    }

    if (!applicant.isProspect) {
      toggleManualAddress();
    }
  }, []);

  useEffect(() => {
    if (!manualAddressShown) {
      methods.unregister([
        "streetNumber",
        "streetName",
        "unitNumber",
        "cityName",
        "provinceName",
        "postalCode",
        "country",
      ]);
      if (currentAddressInfo && currentAddressInfo.streetName.trim() == "") {
        methods.register("autoAddress", {
          required: "Enter your address",
        });
      }
    } else {
      //methods.setValue("autoAddress", "");
      if ((addressSelected && currentAddressInfo) || manualAddressShown) {
        methods.setValue("streetNumber", currentAddressInfo.streetNumber);
        methods.setValue("streetName", currentAddressInfo.streetName);
        methods.setValue("streetType", currentAddressInfo.streetType);
        methods.setValue("streetDirection", currentAddressInfo.streetDirection);
        methods.setValue("unitType", currentAddressInfo.unitType);
        methods.setValue("unitNumber", currentAddressInfo.unitNumber);
        methods.setValue("cityName", currentAddressInfo.city);
        methods.setValue("provinceName", currentAddressInfo.province);
        methods.setValue("postalCode", currentAddressInfo.postalCode);
        methods.setValue("country", currentAddressInfo.country);
      } else {
        methods.setValue("streetNumber", "");
        methods.setValue("streetName", "");
        methods.setValue("streetType", "");
        methods.setValue("streetDirection", "");
        methods.setValue("unitType", "");
        methods.setValue("unitNumber", "");
        methods.setValue("cityName", "");
        methods.setValue("provinceName", "");
        methods.setValue("postalCode", "");
        methods.setValue("country", "");
      }

      methods.unregister("autoAddress");
      methods.register("streetNumber", {
        required: "Enter your street number",
        pattern: {
          value: /^[ #a-zA-Z0-9._-]+$/,
          message: " Please enter a valid street number",
        },
      });
      methods.register("unitNumber", {
        required: methods.getValues("unitType") != undefined && methods.getValues("unitType").trim() != "" ? "Enter your unit number" : false,
        pattern: {
          value: /^[ #a-zA-Z0-9._-]+$/,
          message:
            " Please enter a valid unit number. Only letters, numbers and _-.# are accepted",
        },
      });
      methods.register("unitType", {
        required: methods.getValues("unitNumber") != undefined && methods.getValues("unitNumber").trim() != "" ? "Select a unit type" : false,
        pattern: {
          value: /^[ #a-zA-Z0-9._-]+$/,
          message: "Select a unit type",
        },
        validate: {
          unitType: (c) => {
            if (c.trim() != "" || methods.getValues("unitNumber") == undefined || methods.getValues("unitNumber").trim() == "") {
              return true
            } else {
              return "Select a unit type";
            }
          },
        },
      });
      methods.register("streetName", {
        required: "Enter your street name",
        pattern: {
          value: /^[ #a-zA-ZÀ-Ÿ0-9._-]+$/,
          message: " Please enter a valid street name",
        },
      });
      methods.register("streetType", {
        required: "Select your street type"
      });
      methods.register("cityName", {
        required: "Enter your city name",
        pattern: {
          value: /^[ #a-zA-ZÀ-Ÿ0-9._'-]+$/,
          message: " Please enter a valid city name",
        },
      });
      methods.register("provinceName", {
        required: "Select your province name",
      });
      methods.register("postalCode", {
        required: "Enter your postal code",
      });
      methods.register("country", {
        required: "Select your country name",
      });
      if (residenceYearsText == "0" && residenceMonthsText != "0") {
        methods.setValue("residenceYears", "0");
      }
      if (residenceMonthsText == "0" && residenceYearsText != "0") {
        methods.setValue("residenceMonths", "0");
      }
    }

    if (methods.formState.errors == undefined || Object.keys(methods.formState.errors).length == 0) {
      setDisplayError(false);
    }
  }, [manualAddressShown, methods, currentAddressInfo]);

  const formatAddress = () => {
    const span = document.getElementById("span-street-address");
    const span2 = document.getElementById("span-city");
    const span3 = document.getElementById("span-state");
    const span4 = document.getElementById("span-postcode");
    const span5 = document.getElementById("span-country");

    const streetAddress = span!.innerHTML.split(" ");
    const streetNumber = streetAddress.shift();

    methods.setValue("autoAddress", span!.innerHTML);
    methods.setValue("cityName", span2!.innerHTML);
    methods.setValue("provinceName", span3!.innerHTML);
    methods.setValue("postalCode", span4!.innerHTML);
    methods.setValue("country", span5!.innerHTML);
    methods.setValue("streetName", currentAddressInfo.streetName);
    methods.setValue("residentStatus", applicant.residentStatus);
    methods.setValue("residenceYears", applicant.residenceYears != 0 ? applicant.residenceYears.toString() : "");
    methods.setValue("residenceMonths", applicant.residenceMonths != 0 ? applicant.residenceMonths.toString() : "");


    if (streetNumber?.includes("-")) {
      const unit = streetNumber.split("-")[0];
      const number = streetNumber.split("-")[1];
      methods.setValue("unitNumber", unit!);
      methods.setValue("streetNumber", number!);
    } else {
      methods.setValue("unitNumber", "");
      methods.setValue("streetNumber", streetNumber!);
    }
  };

  const setHomeAddress = async (data: any) => {
    dispatch(setStreetNumber(methods.getValues("streetNumber")));
    dispatch(setStreetName(methods.getValues("streetName")));
    dispatch(setStreetType(methods.getValues("streetType")));
    dispatch(setStreetDirection(methods.getValues("streetDirection")));
    dispatch(setUnitType(methods.getValues("unitType") != undefined ? methods.getValues("unitType").trim() : ""));
    dispatch(setUnitNumber((methods.getValues("unitNumber") != undefined ? methods.getValues("unitNumber") : "")));
    dispatch(setCity(methods.getValues("cityName")));
    dispatch(setProvince(methods.getValues("provinceName")));
    dispatch(setPostalCode(methods.getValues("postalCode")));
    dispatch(setCountry(methods.getValues("country")));
    dispatch(setMailingAddressSame(methods.getValues("isMailingAddressSame")));

    //Update Address Info on Trellis Backend
    if (homeAddress.address.id != 0) {
      await client.apiApplicantsAddressPut(homeAddress.address.id, applicant.id, new AddressDto({
        id: homeAddress.address.id,
        streetNumber: methods.getValues("streetNumber"),
        streetName: methods.getValues("streetName"),
        streetType: methods.getValues("streetType"),
        streetDirection: methods.getValues("streetDirection"),
        unitType: methods.getValues("unitType"),
        unitNumber: methods.getValues("unitNumber"),
        city: methods.getValues("cityName"),
        region: methods.getValues("provinceName"),
        postalCode: methods.getValues("postalCode"),
        country: methods.getValues("country"),
        timeAtResidenceInMonths: (+residenceYears * 12) + +residenceMonths,
        residenceStatusType_Name: residentStatus,
        deliveryMode: undefined,
        modeNumber: undefined,
        deliveryMode2: undefined,
        modeNumber2: undefined,
        isMailing: false
      })).then((result) => {
        console.log("Trellis Address Updated");
      }).catch((error) => {
        console.log("Error updating trellis address");
      });
    } else {
      let newAddress = new AddressDto({
        id: 0,
        streetNumber: methods.getValues("streetNumber"),
        streetName: methods.getValues("streetName"),
        streetType: methods.getValues("streetType"),
        streetDirection: methods.getValues("streetDirection"),
        unitType: methods.getValues("unitType"),
        unitNumber: methods.getValues("unitNumber"),
        city: methods.getValues("cityName"),
        region: methods.getValues("provinceName"),
        postalCode: methods.getValues("postalCode"),
        country: methods.getValues("country"),
        timeAtResidenceInMonths: (+residenceYears * 12) + +residenceMonths,
        residenceStatusType_Name: residentStatus,
        deliveryMode: undefined,
        modeNumber: undefined,
        deliveryMode2: undefined,
        modeNumber2: undefined,
        isMailing: false
      });
      await client.apiApplicantsAddressesPost([newAddress]).then((result) => {
        console.log("Trellis Address Added");
        result.forEach((address) => {
          if (address.residenceStatusType_Name == data.residentStatus) {
            dispatch(setAddressId(address.id ? address.id : 0));
          }
        });
      }).catch((error) => {
        console.log("Error updating trellis address");
      });
    }
  };

  const onError = (errors: object) => {
    console.log(errors);
    setClickInsightFormErrors(errors);
    setDisplayError(true);
    setHighlightError(0);
  }

  useEffect(() => {
    if (currentAddressInfo.streetNumber.trim() != "" && !manualAddressShown) {
      setShowAddress(true);
      setAddressSelected(true);
      setAddressSelectionError(false);
    }

  }, [currentAddressInfo]);

  const onSubmit = async (data: any) => {
    setHighlightError(0);
    if (!addressSelected && !manualAddressShown) {
      setAddressSelectionError(true);
      return;
    }

    if (data.streetType.trim() == "") {
      methods.setError("streetType", {
        type: "required",
        message: "Select your street type",
      });
      return;
    }

    if (!manualAddressShown) {
      formatAddress();
    }

    //*set address complete here
    dispatch(setAddressComplete(true));
    dispatch(setResidentStatus(data.residentStatus));
    dispatch(setResidenceYears(+data.residenceYears));
    dispatch(setResidenceMonths(+data.residenceMonths));
    setIsSaving(true);
    await setHomeAddress(data);
    setIsSaving(false);

    let totalOccupancyTime = (+data.residenceYears * 12) + +data.residenceMonths;
    if (totalOccupancyTime < 36) {
      dispatch(setApplicationProgress(9));
      navigate("/joint/previous-address");
    } else {
      dispatch(clearPreviousAddressState());
      dispatch(setApplicationProgress(10));
      navigate("/joint/employment");
    }
  };

  const focusError = () => {
    if (!manualAddressShown) {
      toggleManualAddress();

      if (methods.getValues("streetNumber") == "") {
        methods.setError("streetNumber", {
          type: "required",
          message: "Enter your street number",
        });
      }
      if (methods.getValues("streetName") == "") {
        methods.setError("streetName", {
          type: "required",
          message: "Enter your street name",
        });
      }
      if (methods.getValues("streetType") == "") {
        methods.setError("streetType", {
          type: "required",
          message: "Select your street type",
        });
      }
      if (methods.getValues("cityName") == "") {
        methods.setError("cityName", {
          type: "required",
          message: "Enter your city name.",
        });
      }
      if (methods.getValues("provinceName") == "") {
        methods.setError("provinceName", {
          type: "required",
          message: "Select your province name",
        });
      }
      if (methods.getValues("postalCode") == "") {
        methods.setError("postalCode", {
          type: "required",
          message: "Enter your postal code",
        });
      }
      if (methods.getValues("country") == "") {
        methods.setError("country", {
          type: "required",
          message: "Select your country name",
        });
      }
    }

    if (methods.formState?.errors?.streetNumber?.message && methods.formState?.errors?.streetNumber?.message != "") {
      setFocusErrorType(1);
      setHighlightError(1);
      try {
        document.getElementById("streetNumber")?.focus();
      } catch { }
    } else if (methods.formState?.errors?.unitNumber?.message && methods.formState?.errors?.unitNumber?.message != "") {
      setFocusErrorType(2);
      setHighlightError(2);
      setHighlightError(2);
      try {
        document.getElementById("unitNumber")?.focus();
      } catch { }
    } else if (methods.formState?.errors?.streetName?.message && methods.formState?.errors?.streetName?.message != "") {
      setFocusErrorType(3);
      setHighlightError(3);
      setHighlightError(3);
      try {
        document.getElementById("street-address")?.focus();
      } catch { }
    } else if (methods.formState?.errors?.streetType?.message && methods.formState?.errors?.streetType?.message != "") {
      setFocusErrorType(4);
      setHighlightError(4);
      setHighlightError(4);
      try {
        document.getElementById("streetType")?.focus();
      } catch { }
    } else if (methods.formState?.errors?.cityName?.message && methods.formState?.errors?.cityName?.message != "") {
      setFocusErrorType(5);
      setHighlightError(5);
      setHighlightError(5);
      try {
        document.getElementById("cityName")?.focus();
      } catch { }
    } else if (methods.formState?.errors?.provinceName?.message && methods.formState?.errors?.provinceName?.message != "") {
      setFocusErrorType(6);
      setHighlightError(6);
      setHighlightError(6);
      try {
        document.getElementById("province")?.focus();
      } catch { }
    } else if (methods.formState?.errors?.postalCode?.message && methods.formState?.errors?.postalCode?.message != "") {
      setFocusErrorType(7);
      setHighlightError(7);
      setHighlightError(7);
      try {
        document.getElementById("postcode")?.focus();
      } catch { }
    } else if (methods.formState?.errors?.country?.message && methods.formState?.errors?.country?.message != "") {
      setFocusErrorType(8);
      setHighlightError(8);
      setHighlightError(8);
      try {
        document.getElementById("country")?.focus();
      } catch { }
    } else if (methods.formState?.errors?.residentStatus?.message && methods.formState?.errors?.residentStatus?.message != "") {
      setFocusErrorType(9);
      setHighlightError(9);
      setHighlightError(9);
      try {
        document.getElementById("residentStatus")?.focus();
      } catch { }
    } else if ((methods.formState?.errors?.residenceYears?.message && methods.formState?.errors?.residenceYears?.message != "") ||
      (methods.formState?.errors?.residenceMonths?.message && methods.formState?.errors?.residenceMonths?.message != "")) {
      setFocusErrorType(10);
      setHighlightError(10);
      setHighlightError(10);
      try {
        document.getElementById("residentYears")?.focus();
      } catch { }
    }

    const timeout = setTimeout(() => {
      setFocusErrorType(0);
    }, 1000)
    return () => clearTimeout(timeout)
  }

  return (
    <>
      <SlideTransition>
        <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
          <div
            className={style.addHeading}
            role="region"
            aria-label="Address Header"
          >
            <div style={{ width: '-webkit-fill-available' }}>
              <h1 aria-label="Address Header">Where do you live?</h1>
              <p className="sub-heading">Add your address to your profile.</p>
            </div>
            {displayError &&
              <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
                <img src="assets/error-info-icon.svg" alt="error" />
                <span className="text-sm ml-2 mt-1">
                  Please edit the highlighted fields before continuing
                  <span className="underline cursor-pointer ml-5" onClick={focusError} style={{ color: "var(--error-red)" }}>Go to the first error</span>
                </span>
              </div>
            }
            <div className="flex flex-row mt-5">
              <FormProvider {...methods}>
                <form
                  autoComplete="off"
                  onSubmit={methods.handleSubmit(onSubmit, onError)}
                >
                  <div className="col-span-1">
                    <div>
                      <label htmlFor="search">Search for your address</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/magnifying-glass-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.autoAddress
                                ? "hidden"
                                : "",
                              "w-5 h-5 absolute top-5 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="search"
                            height={5}
                            width={5}
                          />
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.autoAddress
                                ? ""
                                : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <input
                          autoComplete="nope"
                          className={clsx(
                            methods.formState?.errors?.autoAddress?.message
                              ? "error"
                              : "",
                            "mt-2"
                          )}
                          id="search"
                          type="text"
                          placeholder="Start typing address"
                          {...methods.register("autoAddress")}
                        />

                      </div>
                      {addressSelectionError && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          You must select an address from the list of options
                        </span>
                      )}
                      <div
                        className={clsx("ml-2 mt-2", showAddress ? "" : "hidden")}
                      >
                        <p id="formatted-address"></p>
                        <span id="span-street-address"></span>
                        <br />
                        <span id="span-city"></span>&nbsp;
                        <span id="span-state"></span>&nbsp;
                        <span id="span-postcode"></span>
                        <br />
                        <span id="span-country"></span>
                      </div>
                      <div
                        className="flex relative mt-3"
                      >
                        <p className="active underline cursor-pointer" onClick={() => toggleManualAddress()}>{manualAddressShown ? "Hide address fields" : "Enter address manually"}</p>
                      </div>
                    </div>

                    <AnimatePresence>
                      {manualAddressShown &&
                        <motion.div
                          key={0}
                          initial={{ y: -100, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ opacity: 0 }}
                        ><ManualAddress addressData={currentAddressInfo} manualAddressToggled={manualAddressShown}
                          focusErrorType={focusErrorType} highlightError={highlightError} primaryAddress={true} /></motion.div>
                      }
                    </AnimatePresence>
                  </div>
                  {/* <div className="mt-5 flex flex-col-reverse">
                  <label></label>
                  <input
                    id="mailingIsSame"
                    type="checkbox"
                    value={isMailAddressChecked ? "true" : "false"}
                    className="accent"
                    {...methods.register("isMailingAddressSame", {
                      onChange: toggleMailingAddress,
                    })}
                    defaultChecked
                  />
                  <label htmlFor="mailingIsSame">
                    <p className="sub-heading ml-7 -mt-5">
                      Mailing address is the same as home address
                    </p>
                  </label>
                </div>
                <div
                  className={clsx(
                    isMailAddressChecked ? "hidden" : "",
                    "flex relative"
                  )}
                >
                  <div className="mt-5">
                    <MailingAddress mailingAddressSelectionError={mailingAddressSelectionError} mailingAddressSelected={setMailingAddressHandler}
                      manualAddressToggled={toggleManualMailingAddress} />
                  </div>
                </div> */}
                  <div className="flex md:flex-row flex-col w-max mt-5">
                    <div>
                      <label htmlFor="status">Living Situation</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.residentStatus ? "" : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[420px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <select
                          id="status"
                          {...methods.register("residentStatus", {
                            required: "Select an option",
                            onChange: residentStatusChangeHandler
                          })}
                          onChange={residentStatusChangeHandler}
                          value={residentStatusText}
                          className={clsx(
                            "", "mt-2 pt-2.5"
                          )}
                        >
                          <option hidden value="">
                            Choose...
                          </option>
                          <option value="own">Own</option>
                          <option value="rent">Rent</option>
                          <option value="liveswithparents">Live with Parents</option>
                          <option value="board">Board</option>
                        </select>
                      </div>
                      {methods.formState?.errors?.residentStatus?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.residentStatus.message}
                        </span>
                      )}
                    </div>
                    <div className="md:ml-4">
                      <label htmlFor="title">How long have you lived here?</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.residenceYears ? "" : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[70px] xs:max-md:ml-[370px] md:ml-[120px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <div className="flex">
                          <input
                            className={clsx(
                              "mt-2", style.residentPeriod
                            )}
                            type="number"
                            min={0}
                            placeholder="year(s)"
                            max={99}
                            id="residentLengthYears"
                            value={residenceYearsText}
                            {...methods.register("residenceYears", {
                              required: "Enter your time at this address.",
                              onChange: residenceYearsChangeHandler
                            })}
                            onChange={residenceYearsChangeHandler}
                            onFocus={(e) => e.target.select()}
                          />
                          {residenceYearsText != "" &&
                            <span style={{ position: "absolute", top: "19px", left: residenceYearsText.length > 1 ? "28px" : "22px" }}>year{+residenceYearsText != 1 ? "s" : ""}</span>
                          }
                        </div>
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.residenceMonths ? "" : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[120px] xs:max-md:ml-[370px] md:ml-[140px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <div className="flex">
                          <input
                            className={clsx(
                              "mt-2 ml-2", style.residentPeriod
                            )}
                            type="number"
                            min={0}
                            max={11}
                            placeholder="month(s)"
                            id="residentLengthMonths"
                            value={residenceMonthsText}
                            {...methods.register("residenceMonths", {
                              required: "Enter the number of months you lived in your current residence.",
                              onChange: residenceMonthsChangeHandler
                            })}
                            onChange={residenceMonthsChangeHandler}
                            onFocus={(e) => e.target.select()}
                          />
                          {residenceMonthsText != "" &&
                            <span style={{ position: "absolute", top: "19px", left: residenceMonthsText.length > 1 ? "201px" : "195px" }}>month{+residenceMonthsText != 1 ? "s" : ""}</span>
                          }
                        </div>
                      </div>
                      {methods.formState?.errors?.residenceYears?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.residenceYears.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex max-sm:hidden">
                    <div className="mb-5 mr-5">
                      <button onClick={() => navigate(-1)}
                        type="button"
                        className="back mt-5 w-169 sm:w-28 h-11 px-6">
                        Back
                      </button>
                    </div>
                    <div className="mb-5">
                      <button
                        type="submit"
                        className="primary mt-5 w-full sm:w-28 h-11 px-6"
                        disabled={isSaving}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                        Continue
                      </button>
                    </div>
                  </div>
                  <div className="sm:hidden">
                    <div className="mb-5">
                      <button
                        type="submit"
                        className="primary mt-5 w-full sm:w-28 h-11 px-6"
                        disabled={isSaving}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                        Continue
                      </button>
                    </div>
                    <div className="mb-5">
                      <button onClick={() => navigate(-1)}
                        type="button"
                        className="back mt-5 w-full sm:w-28 h-11 px-6">
                        Back
                      </button>
                    </div>
                  </div>
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
      </SlideTransition>
    </>
  );
}